import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import nqLogo from "../assets/nextq-logo.png";

var useStyles = makeStyles(function () {
  return {
    root: {
      position: "relative",
      width: "250px",
      minWidth: "250px",
      background: "#E8EEF7",
      borderRight: "1px solid #D4DDED",
      display: "flex",
      flexDirection: "column",
      transition: "margin-left 300ms cubic-bezier(0.4,0,0.2,1)",
      overflow: "hidden",
      "@media (max-width: 768px)": {
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        zIndex: 1000,
      },
    },
    collapseBtn: {
      position: "absolute",
      top: "16px",
      right: "12px",
      width: "28px",
      height: "28px",
      borderRadius: "8px",
      border: "1px solid #C8D5E8",
      background: "rgba(255,255,255,0.7)",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 150ms ease-out",
      zIndex: 2,
      "&:hover": {
        background: "#fff",
        borderColor: "#A8BCD8",
      },
    },
    rootClosed: {
      marginLeft: "-250px",
    },
    backdrop: {
      display: "none",
      "@media (max-width: 768px)": {
        display: "block",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.3)",
        zIndex: 999,
      },
    },
    logoHeader: {
      padding: "16px 14px 12px",
      borderBottom: "1px solid #D4DDED",
    },
    newChatButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "9px 14px",
      background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
      border: "none",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#fff",
      width: "calc(100% - 28px)",
      margin: "12px 14px",
      cursor: "pointer",
      fontFamily: "'DM Sans', sans-serif",
      boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
      transition: "all 150ms",
      "&:hover": {
        boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
      },
    },
    newChatButtonActive: {},
    sectionLabel: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#7A8BA8",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      padding: "0 16px",
      marginBottom: "6px",
      fontFamily: "'DM Sans', sans-serif",
    },
    conversationList: {
      flex: 1,
      overflowY: "auto",
      padding: "0 8px",
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#B8C7DC",
        borderRadius: "2px",
      },
    },
    groupHeader: {
      fontSize: "10px",
      fontWeight: 600,
      color: "#7A8BA8",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      padding: "8px 8px 4px",
      fontFamily: "'DM Sans', sans-serif",
    },
    conversationItem: {
      padding: "10px 12px",
      borderRadius: "8px",
      cursor: "pointer",
      marginBottom: "2px",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      transition: "background 100ms",
      fontFamily: "'DM Sans', sans-serif",
      "&:hover": {
        background: "rgba(255,255,255,0.6)",
      },
      "&:hover $itemActions": {
        opacity: 1,
      },
    },
    conversationItemActive: {
      background: "#fff",
      borderLeft: "2px solid #2563EB",
      color: "#1E3A5F",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    },
    itemContent: {
      flex: 1,
      minWidth: 0,
    },
    itemTitle: {
      fontSize: "13px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: "#4B5E7A",
    },
    itemTitleActive: {
      color: "#1e3a5f",
      fontWeight: 500,
    },
    itemDate: {
      fontSize: "11px",
      color: "#8A9BBD",
      marginTop: "2px",
    },
    itemActions: {
      display: "flex",
      alignItems: "center",
      gap: "2px",
      opacity: 0,
      transition: "opacity 120ms",
      flexShrink: 0,
    },
    actionButton: {
      padding: "2px",
    },
    renameInput: {
      fontSize: "13px",
      padding: "2px 4px",
      border: "1px solid #2563eb",
      borderRadius: "4px",
      width: "100%",
      outline: "none",
      fontFamily: "inherit",
    },
    emptyState: {
      fontSize: "13px",
      color: "#7A8BA8",
      padding: "16px",
      textAlign: "center",
    },
    loadingWrap: {
      display: "flex",
      justifyContent: "center",
      padding: "24px 0",
    },
  };
});

function groupByDate(conversations) {
  var now = new Date();
  var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  var yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  var weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  var groups = { Today: [], Yesterday: [], "This Week": [], Older: [] };

  conversations.forEach(function (c) {
    var d = new Date(c.updated_at || c.created_at);
    if (d >= today) groups.Today.push(c);
    else if (d >= yesterday) groups.Yesterday.push(c);
    else if (d >= weekAgo) groups["This Week"].push(c);
    else groups.Older.push(c);
  });

  return groups;
}

function formatItemDate(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

var ChatSidebar = function ChatSidebar({
  open,
  conversations,
  activeConversationId,
  loadingConversations,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  onClose,
}) {
  var classes = useStyles();
  var _editingId = useState(null);
  var editingId = _editingId[0];
  var setEditingId = _editingId[1];
  var _editTitle = useState("");
  var editTitle = _editTitle[0];
  var setEditTitle = _editTitle[1];

  var groups = useMemo(
    function () {
      if (!conversations || !conversations.length) return null;
      return groupByDate(conversations);
    },
    [conversations]
  );

  function handleStartRename(e, conv) {
    e.stopPropagation();
    setEditingId(conv.id);
    setEditTitle(conv.title || "");
  }

  function handleRenameKeyDown(e, id) {
    if (e.key === "Enter") {
      if (editTitle.trim() && onRenameConversation) {
        onRenameConversation(id, editTitle.trim());
      }
      setEditingId(null);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  }

  function handleRenameBlur(id) {
    if (editTitle.trim() && onRenameConversation) {
      onRenameConversation(id, editTitle.trim());
    }
    setEditingId(null);
  }

  function handleDelete(e, id) {
    e.stopPropagation();
    if (onDeleteConversation) {
      onDeleteConversation(e, id);
    }
  }

  var isNewChat = !activeConversationId;
  var newChatClass = classes.newChatButton + (isNewChat ? " " + classes.newChatButtonActive : "");

  var groupOrder = ["Today", "Yesterday", "This Week", "Older"];

  return (
    <>
      {open && <div className={classes.backdrop} onClick={onClose} />}
      <div className={classes.root + (!open ? " " + classes.rootClosed : "")}>
        <button className={classes.collapseBtn} onClick={onClose} title="Close sidebar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="11 17 6 12 11 7" />
            <line x1="18" y1="12" x2="6" y2="12" />
          </svg>
        </button>
        <div className={classes.logoHeader}>
        <img src={nqLogo} alt="NextQuarter" style={{ height: 22 }} />
      </div>

      <button className={newChatClass} onClick={onNewChat}>
        <span style={{ fontSize: "15px", lineHeight: 1 }}>+</span>
        New Chat
      </button>

      <div className={classes.sectionLabel}>Recent</div>

      <div className={classes.conversationList}>
        {loadingConversations ? (
          <div className={classes.loadingWrap}>
            <CircularProgress size={20} style={{ color: "#9ca3af" }} />
          </div>
        ) : !conversations || conversations.length === 0 ? (
          <div className={classes.emptyState}>No conversations yet</div>
        ) : (
          groups &&
          groupOrder.map(function (groupName) {
            var groupConvs = groups[groupName];
            if (!groupConvs || groupConvs.length === 0) return null;

            return (
              <div key={groupName}>
                <div className={classes.groupHeader}>{groupName}</div>
                {groupConvs.map(function (conv) {
                  var isActive = conv.id === activeConversationId;
                  var itemClass =
                    classes.conversationItem +
                    (isActive ? " " + classes.conversationItemActive : "");
                  var titleClass =
                    classes.itemTitle + (isActive ? " " + classes.itemTitleActive : "");

                  return (
                    <div
                      key={conv.id}
                      className={itemClass}
                      onClick={function () {
                        onSelectConversation(conv.id);
                      }}
                      onDoubleClick={function (e) {
                        handleStartRename(e, conv);
                      }}
                    >
                      <div className={classes.itemContent}>
                        {editingId === conv.id ? (
                          <input
                            className={classes.renameInput}
                            value={editTitle}
                            onChange={function (e) {
                              setEditTitle(e.target.value);
                            }}
                            onKeyDown={function (e) {
                              handleRenameKeyDown(e, conv.id);
                            }}
                            onBlur={function () {
                              handleRenameBlur(conv.id);
                            }}
                            autoFocus
                            onClick={function (e) {
                              e.stopPropagation();
                            }}
                          />
                        ) : (
                          <div className={titleClass}>{conv.title || "Untitled"}</div>
                        )}
                        <div className={classes.itemDate}>
                          {formatItemDate(conv.updated_at || conv.created_at)}
                        </div>
                      </div>
                      <div className={classes.itemActions}>
                        <IconButton
                          className={classes.actionButton}
                          size="small"
                          onClick={function (e) {
                            handleStartRename(e, conv);
                          }}
                        >
                          <EditOutlinedIcon style={{ fontSize: 14, color: "#9ca3af" }} />
                        </IconButton>
                        <IconButton
                          className={classes.actionButton}
                          size="small"
                          onClick={function (e) {
                            handleDelete(e, conv.id);
                          }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 14, color: "#9ca3af" }} />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
    </>
  );
};

export default ChatSidebar;
